// Reports — every submission collected across Tasks and Inspections, grouped by
// the form template that captured it. Rows ARE form templates (name + folder +
// published date + last entry + entry count), so the list reuses
// getFormTemplates rather than declaring its own endpoint.
//
// Deliberate gaps this chunk, all pending backend contracts:
// - No entries filter: the list is narrowed to published forms (`published:
//   true`), but POST /forms/all still has no "has submissions" param, so a
//   published form with zero entries is listed and counted.
// - The list rows carry no per-row bookmark flag, so the design's glyph beside
//   each name is absent. (The Bookmarks tab itself is live — it filters on
//   `bookmark: true`; a filter param gives no per-row state.)
//
// Row kebab + selection bar carry the real actions: View submissions (the same
// navigation a row click takes; single-select in the bar) and Bookmark /
// Remove bookmark — PATCH /forms/bookmark via the Forms page's mutation, the
// tab picking the body value since rows carry no flag to toggle off from.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Button,
  Card,
  DataTable,
  FilterDropdown,
  Icon,
  IconButton,
  Input,
  TableFooter,
  Tabs,
  type FilterDropdownCategory,
  type FilterDropdownDateRange,
} from '@/components/common';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import useStickyTotal from '@/hooks/useStickyTotal';
import {
  FormDateFilter,
  FormSortKey,
  useBookmarkFormsMutation,
  useGetFormTemplatesQuery,
  type FormTemplate,
} from '@/api/endpoints/formsApi';
import { useGetFormFoldersQuery } from '@/api/endpoints/formFoldersApi';
import {
  DATE_ANY,
  buildDateFilterOptions,
  dateFilterOf,
  dateRangeOf,
} from '@/utils/dateFilterPresets';
import { useReportColumns } from './reportColumns';

type SortType = 'asc' | 'desc';
type ReportTab = 'submitted' | 'bookmarks';

// UI sort key (sortable column headers) → backend orderBy.key.
const SORT_API_KEY: Record<string, FormSortKey> = {
  name: FormSortKey.Title,
  published: FormSortKey.PublishedDate,
  lastEntry: FormSortKey.LastEntryDate,
  entries: FormSortKey.NumberOfEntries,
};

// Toolbar Filter category keys (rail order) + the empty selection shape.
const FILTER_KEYS = ['folder', 'publishedDate', 'lastEntry'] as const;
const emptyFilters = (): Record<string, Set<string>> =>
  Object.fromEntries(FILTER_KEYS.map((k) => [k, new Set<string>()]));

const ReportsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ReportTab>('submitted');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  // Newest activity first, matching the design's active header.
  const [sort, setSort] = useState<{ key: string; type: SortType }>({
    key: 'lastEntry',
    type: 'desc',
  });
  // id → row: the selection bar's single-report action needs the row's own
  // values, and a row picked on page 1 is no longer in `items` after paging.
  const [selected, setSelected] = useState<Map<string, FormTemplate>>(new Map());
  const [filters, setFilters] = useState<Record<string, Set<string>>>(emptyFilters);
  // Custom-range FROM/TO per date category (`publishedDate` / `lastEntry`).
  const [dateRanges, setDateRanges] = useState<Record<string, FilterDropdownDateRange>>({});

  const bookmarksTab = activeTab === 'bookmarks';

  // Folder options — fetched up-front so the pane is ready on first open.
  const { data: folders = [] } = useGetFormFoldersQuery();

  const filterCategories: FilterDropdownCategory[] = useMemo(() => {
    const dateOptions = buildDateFilterOptions(t);
    return [
      {
        key: 'folder',
        label: t('table_header_folder'),
        icon: 'folder',
        searchable: true,
        searchPlaceholder: t('reports_filter_folder_ph'),
        options: folders.map((f) => ({ id: f.id, label: f.title })),
      },
      {
        key: 'publishedDate',
        label: t('table_header_published_date'),
        icon: 'calendar',
        type: 'single',
        rangeOptionId: FormDateFilter.CUSTOM,
        defaultOptionId: DATE_ANY,
        options: dateOptions,
      },
      {
        key: 'lastEntry',
        label: t('table_header_last_entry'),
        icon: 'inbox',
        type: 'single',
        rangeOptionId: FormDateFilter.CUSTOM,
        defaultOptionId: DATE_ANY,
        options: dateOptions,
      },
    ];
  }, [t, folders]);

  const publishedDate = dateFilterOf(filters, 'publishedDate');
  const lastEntry = dateFilterOf(filters, 'lastEntry');

  const { data, currentData, isLoading, isError, isFetching } = useGetFormTemplatesQuery(
    {
      status: ['active'],
      page,
      limit,
      // A report IS a published form — an unpublished template can't have
      // collected anything. Always true here; the Forms list omits the param.
      published: true,
      // Omitted on Submitted reports, not sent as false: bookmarked reports
      // still belong in the default list (the design shows their glyphs there).
      bookmarked: bookmarksTab || undefined,
      folder: filters.folder.size ? [...filters.folder] : undefined,
      publishedDate,
      publishedDateStart: dateRangeOf(dateRanges, 'publishedDate', publishedDate, 'start'),
      publishedDateEnd: dateRangeOf(dateRanges, 'publishedDate', publishedDate, 'end'),
      lastEntry,
      lastEntryStart: dateRangeOf(dateRanges, 'lastEntry', lastEntry, 'start'),
      lastEntryEnd: dateRangeOf(dateRanges, 'lastEntry', lastEntry, 'end'),
      search: debouncedSearch || undefined,
      orderBy: {
        key: SORT_API_KEY[sort.key] ?? FormSortKey.LastEntryDate,
        type: sort.type,
      },
    },
    { refetchOnMountOrArgChange: true },
  );

  // A new search / filter / sort shrinks or reorders the result set — snap back
  // to page 1.
  useEffect(() => setPage(1), [debouncedSearch, filters, dateRanges, sort]);

  const items = data?.items ?? [];
  // The API reports the record count on page-1 responses only (absent on
  // page 2+) — record it there and keep serving it while paging.
  const total = useStickyTotal(currentData?.total, page);
  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);

  // One navigation for a row click, the kebab's View and the bar's View — the
  // submissions screen needs the form's identity plus the two figures the
  // submissions endpoint can't answer (total entries + last entry).
  const openSubmissions = (report: FormTemplate) =>
    navigate('/submissions', {
      state: {
        formId: report.id,
        formName: report.name,
        entries: report.entries,
        lastEntry: report.lastEntry,
      },
    });

  // The tab picks the body value: rows carry no bookmark flag, so there is no
  // per-row current state — on Bookmarks membership IS the state and the action
  // removes; elsewhere it adds. A run from the selection bar clears the
  // selection; a row run leaves it be (the Forms page split).
  const [bookmarkForms, { isLoading: bookmarking }] = useBookmarkFormsMutation();
  const runBookmark = async (formIds: string[], fromSelection: boolean) => {
    if (!formIds.length) return;
    const bookmark = !bookmarksTab;
    try {
      await bookmarkForms({ formIds, bookmark }).unwrap();
      toast.success(
        t(bookmark ? 'forms_bookmark_done' : 'forms_unbookmark_done', { count: formIds.length }),
      );
      if (fromSelection) setSelected(new Map());
      // Removals drop the rows out of the Bookmarks tab on refetch. Emptying the
      // current page would leave `page` past the end — TableFooter recomputes its
      // page count but never calls onPage — so step back one.
      if (!bookmark && page > 1 && formIds.length >= items.length) setPage((p) => p - 1);
    } catch {
      toast.error(t('error_generic'));
    }
  };

  const columns = useReportColumns({
    onView: openSubmissions,
    onBookmark: (report) => runBookmark([report.id], false),
    bookmarked: bookmarksTab,
  });

  const switchTab = (value: string) => {
    setActiveTab(value as ReportTab);
    setPage(1);
    setSearch('');
    setFilters(emptyFilters());
    setDateRanges({});
    setSelected(new Map());
  };

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else {
        const row = items.find((r) => r.id === id);
        if (row) next.set(id, row);
      }
      return next;
    });

  // Header select-all — scoped to the current page's rows.
  const allSelected = items.length > 0 && items.every((r) => selected.has(r.id));
  const someSelected = items.some((r) => selected.has(r.id));
  const toggleSelectAll = () =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (allSelected) items.forEach((r) => next.delete(r.id));
      else items.forEach((r) => next.set(r.id, r));
      return next;
    });

  // DataTable keys row checks off a Set; the single-report bar action needs the
  // one picked row's facts.
  const selectedIdSet = useMemo(() => new Set(selected.keys()), [selected]);
  const onlySelected = selected.size === 1 ? [...selected.values()][0] : undefined;

  // Same key toggles direction; a new key resets to desc.
  const handleSort = (key: string) =>
    setSort((prev) =>
      prev.key === key
        ? { key, type: prev.type === 'asc' ? 'desc' : 'asc' }
        : { key, type: 'desc' },
    );

  return (
    <div className='reports-page'>
      <header className='reports-page__header'>
        <div>
          <h1 className='reports-page__title'>{t('reports_title')}</h1>
          <p className='reports-page__subtitle'>{t('reports_subtitle')}</p>
        </div>
      </header>

      <Tabs
        aria-label={t('reports_title')}
        value={activeTab}
        onChange={switchTab}
        items={[
          {
            value: 'submitted',
            label: t('reports_tab_submitted'),
            icon: 'file-check',
            count: bookmarksTab ? undefined : total,
          },
          {
            value: 'bookmarks',
            label: t('reports_tab_bookmarks'),
            icon: 'bookmark',
            count: bookmarksTab ? total : undefined,
          },
        ]}
      />

      <div className='reports-page__toolbar'>
        <div className='reports-page__toolbar-lead'>
          <div className='reports-page__search'>
            <Input
              size='sm'
              iconStart='search'
              placeholder={t('reports_search_ph')}
              aria-label={t('field_search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <IconButton
                className='reports-page__search-clear'
                icon='x'
                variant='ghost'
                size='sm'
                aria-label={t('reports_search_clear')}
                onClick={() => setSearch('')}
              />
            )}
          </div>
          <FilterDropdown
            label={t('action_filter')}
            categories={filterCategories}
            value={filters}
            ranges={dateRanges}
            onApply={(next, ranges) => {
              setFilters(next);
              setDateRanges(ranges);
            }}
            emptyText={t('text_no_results_found')}
          />
        </div>
        <span className='reports-page__count'>{t('reports_count', { count: total })}</span>
      </div>

      <Card padded={false} className='reports-page__panel'>
        {selected.size > 0 && (
          <div className='reports-page__selection-bar'>
            <div className='reports-page__selection-lead'>
              <span className='reports-page__selected'>
                {t('reports_selected_count', { count: selected.size })}
              </span>
              <span className='reports-page__selection-divider' />
              {/* View submissions targets one report — hidden while 2+ are picked. */}
              {onlySelected && (
                <Button
                  variant='ghost'
                  iconStart='table-2'
                  onClick={() => openSubmissions(onlySelected)}
                >
                  {t('reports_action_view_submissions')}
                </Button>
              )}
              <Button
                variant='ghost'
                iconStart={bookmarksTab ? 'bookmark-x' : 'bookmark'}
                disabled={bookmarking}
                onClick={() => runBookmark([...selected.keys()], true)}
              >
                {t(bookmarksTab ? 'forms_action_unbookmark' : 'forms_action_bookmark')}
              </Button>
            </div>
            <Button variant='ghost' onClick={() => setSelected(new Map())}>
              {t('button_clear')}
            </Button>
          </div>
        )}

        <DataTable
          columns={columns}
          rows={items}
          rowKey={(r) => r.id}
          selectable
          selectedIds={selectedIdSet}
          onToggleSelect={toggleSelect}
          allSelected={allSelected}
          someSelected={someSelected}
          onToggleSelectAll={toggleSelectAll}
          selectAllLabel={t('reports_select_all')}
          sort={sort}
          onSort={handleSort}
          loading={isLoading || isFetching}
          error={isError}
          loadingText={t('text_loading')}
          errorText={t('error_generic')}
          minWidth={1100}
          onRowClick={openSubmissions}
          empty={
            <>
              <Icon name={bookmarksTab ? 'bookmark' : 'file-chart-column'} size={26} />
              {t(bookmarksTab ? 'reports_bookmarks_empty' : 'reports_empty')}
            </>
          }
        />

        <TableFooter
          page={page}
          limit={limit}
          total={total}
          limits={[10, 20, 50]}
          onPage={setPage}
          onLimit={(n) => {
            setLimit(n);
            setPage(1);
          }}
          perPageLabel={t('reports_items_per_page')}
          rangeText={t('reports_range', { from, to, total })}
        />
      </Card>
    </div>
  );
};

export default ReportsPage;
